import { useContext, useEffect, useState } from 'react'

import { Heading } from '@hhgtech/hhg-components'
import { Box, BoxProps } from '@mantine/core'
import { useIntl } from 'react-intl'

import { callApiNew } from 'api/axios'
import { ecommerceDataFormatter } from 'api/dataTransform/ecommerce'
import { PATHS } from 'api/paths'
import { CarouselOrListingBlockWrapper } from 'components/organisms/serviceConnections/carouselOrListingBlockWrapper'
import { CustomerCartManager, GuestCartManager } from 'extentions/cartManager'
import { Product } from 'interfaces/types/Ecommerce'
import { actionTypes } from 'state/actionTypes'
import { AppContext } from 'state/appContext'

import useStyles from './index.styles'
import { ProductCard } from './productCard'
import { ProductWideCard } from './productWideCard'
import { showNotification } from '../notificationsHandler'

type LayoutType = 'vertical' | 'horizon' | 'wide-horizon'

type Props = {
  skuList?: string
  products?: Product[]
  heading?: string
  layout?: LayoutType[]
  numberItemToSlideDesktop?: number
  numberItemToSlideMobile?: number
  cartBtnColor?: string
} & BoxProps

const ProductsWidget = ({
  skuList = '',
  products: productsProp = [],
  styles,
  className,
  classNames,
  heading,
  layout,
  numberItemToSlideDesktop = 4,
  numberItemToSlideMobile = 3,
  cartBtnColor,
  ...boxProps
}: Props) => {
  const { formatMessage: f } = useIntl()
  const {
    state: { userInfo, cartNumber: cartNumberInit },
    dispatch,
  } = useContext(AppContext)
  const cartNumber = cartNumberInit || 0
  const [products, setProducts] = useState<Product[]>([])
  const { classes, cx } = useStyles(undefined, {
    name: 'ProductsWidget',
    styles,
    classNames,
  })

  useEffect(() => {
    productsProp.length === 0
      ? skuList.split(',').length > 0 &&
        (async () => {
          const res = await callApiNew(
            PATHS.ECOMMERCE.GET_PRODUCT_DETAIL_BY_LIST_IDS,
            {
              params: {
                skuList: skuList.replace(/ /g, ''),
              },
            },
            'ecommerce',
          )
          setProducts(
            (res?.data?.products || []).map(
              ecommerceDataFormatter.formatProduct,
            ),
          )
        })()
      : setProducts(productsProp)
  }, [skuList])

  const addToCart = async (
    { amount, sku }: { amount: number; sku: number | string },
    onSuccess?: (res?: any) => void,
    onFailure?: (res?: any) => void,
  ) => {
    let res
    if (userInfo?.id) {
      res = await CustomerCartManager.addToCart(String(sku), amount)
    } else {
      res = await GuestCartManager.addToCart(String(sku), amount)
    }
    if (res?.status === 1) {
      onSuccess?.(res)
    } else {
      onFailure?.(res)
    }
  }

  const handleAddToCart = async (amount: number, sku: number | string) =>
    addToCart(
      { amount, sku },
      () => {
        showNotification({
          title: f({ id: 'productInfo.addedToCart' }),
          message: '',
          type: 'success',
        })
        dispatch({
          type: actionTypes.UPDATE_CART,
          payload: cartNumber + amount,
        })
      },
      () =>
        showNotification({
          message: f({
            id: 'notify.error.message',
            defaultMessage: 'Đã xảy ra lỗi, vui lòng thử lại',
          }),
          type: 'danger',
        }),
    )

  const handleBuyNow = async (amount: number, sku: number | string) =>
    addToCart(
      { amount, sku },
      () => {
        showNotification({
          title: f({ id: 'productInfo.addedToCart' }),
          message: '',
          type: 'success',
        })
        dispatch({
          type: actionTypes.UPDATE_CART,
          payload: cartNumber + 1,
        })
        window.open(`/shop/gio-hang/`)
      },
      () =>
        showNotification({
          message: f({
            id: 'notify.error.message',
            defaultMessage: 'Đã xảy ra lỗi, vui lòng thử lại',
          }),
          type: 'danger',
        }),
    )

  const layoutType = layout?.[products.length - 1] || 'vertical'

  return products.length > 0 ? (
    <Box className={cx(classes.root, className)} {...boxProps}>
      {heading && (
        <Box className={classes.heading}>
          <Heading tag="h4" className={cx('title', classes.headingText)}>
            {heading}
          </Heading>
        </Box>
      )}
      <CarouselOrListingBlockWrapper
        showFullSlide
        lengthToCarouselDesktop={numberItemToSlideDesktop}
        lengthToCarouselMobile={numberItemToSlideMobile}
      >
        <CarouselOrListingBlockWrapper.Slider>
          {products.map((i, idx) =>
            layoutType === 'wide-horizon' ? (
              <ProductWideCard
                key={String(idx)}
                cartBtnColor={cartBtnColor}
                classNames={{
                  root: classes.cardRoot,
                  thumbnail: classes.cardThumbnail,
                }}
                product={i}
                onAddToCart={handleAddToCart}
                onBuyNow={handleBuyNow}
              />
            ) : (
              <ProductCard
                key={String(idx)}
                className={classes.cardRoot}
                product={i}
                onAddToCart={handleAddToCart}
                onBuyNow={handleBuyNow}
                cartBtnColor={cartBtnColor}
                layout={layoutType}
              />
            ),
          )}
        </CarouselOrListingBlockWrapper.Slider>
      </CarouselOrListingBlockWrapper>
    </Box>
  ) : null
}

export { ProductsWidget }
